import type { NavLayout } from '@khaddi/contracts';
import { useState } from 'react';
import { Pressable, View } from 'react-native';
import { useSession } from '../session';
import { colors, radii } from '../tokens';
import { useLayout } from './layout';
import { Text } from './primitives';

const OPTIONS: { value: NavLayout; label: string }[] = [
  { value: 'side', label: 'Menu bên trái' },
  { value: 'top', label: 'Menu trên cùng' },
];

/** Tiny wireframe of the layout: a nav strip (darker) beside or above the content blocks. */
const Sketch = ({ layout, on }: { layout: NavLayout; on: boolean }) => {
  const nav = on ? colors.primary : colors.borderStrong;
  const block = on ? colors.surfaceMuted : colors.divider;
  return (
    <View style={{ height: 56, borderRadius: radii.xs, borderWidth: 1, borderColor: colors.border, backgroundColor: colors.surface, padding: 4, gap: 4, flexDirection: layout === 'side' ? 'row' : 'column' }}>
      <View style={layout === 'side' ? { width: 14, borderRadius: 3, backgroundColor: nav } : { height: 9, borderRadius: 3, backgroundColor: nav }} />
      <View style={{ flex: 1, gap: 4 }}>
        <View style={{ flex: 1, borderRadius: 3, backgroundColor: block }} />
        <View style={{ flex: 1, borderRadius: 3, backgroundColor: block }} />
      </View>
    </View>
  );
};

/** "Giao diện" card for the account screen: side or top nav on wide screens, saved on the account. */
export const LayoutPicker = () => {
  const { user, updatePrefs } = useSession();
  const { navLayout, wide } = useLayout();
  const [failed, setFailed] = useState(false);
  if (!user) return null;

  const choose = (value: NavLayout) => {
    if (value === navLayout) return;
    setFailed(false);
    updatePrefs({ navLayout: value }).catch(() => setFailed(true));
  };

  return (
    <View style={{ backgroundColor: colors.surface, borderRadius: radii.lg, padding: 14, gap: 10 }}>
      <View style={{ gap: 2 }}>
        <Text variant="heading">Giao diện</Text>
        {!wide ? <Text variant="caption">Áp dụng khi dùng màn hình lớn</Text> : null}
      </View>
      <View accessibilityRole="radiogroup" aria-label="Giao diện" style={{ flexDirection: 'row', gap: 10 }}>
        {OPTIONS.map((option) => {
          const on = option.value === navLayout;
          return (
            <Pressable
              key={option.value}
              accessibilityRole="radio"
              accessibilityState={{ checked: on }}
              accessibilityLabel={option.label}
              onPress={() => choose(option.value)}
              style={({ pressed }) => ({
                flex: 1,
                minHeight: 44,
                borderRadius: radii.md,
                borderWidth: 2,
                borderColor: on ? colors.primaryText : colors.border,
                backgroundColor: on ? colors.surfaceMuted : colors.surface,
                padding: 10,
                gap: 8,
                transform: [{ scale: pressed ? 0.98 : 1 }],
              })}
            >
              <Sketch layout={option.value} on={on} />
              <Text variant="bodyStrong" color={on ? colors.primaryText : colors.text} style={{ fontSize: 13, textAlign: 'center' }}>
                {option.label}
              </Text>
            </Pressable>
          );
        })}
      </View>
      {failed ? (
        <View accessibilityRole="alert">
          <Text variant="caption" color={colors.danger} style={{ fontWeight: '600' }}>
            Chưa lưu được giao diện, thử lại
          </Text>
        </View>
      ) : null}
    </View>
  );
};
